import { useEffect, useRef } from 'react';

/**
 * Dismiss an overlay with the Escape key.
 *
 * Every dialog, drawer and sheet in the app can be closed by pointer — the
 * backdrop, or the close button in its header. This is the keyboard half of that
 * contract, so a keyboard-only user is never stranded inside an overlay.
 *
 * The handler is held in a ref, so passing an inline arrow does not re-subscribe
 * the listener on every render. The listener sits on `document` in the capture
 * phase to fire even while focus is inside a field that handles its own keys.
 */
export function useEscapeKey(onEscape: () => void): void {
  const handler = useRef(onEscape);
  handler.current = onEscape;

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handler.current();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);
}
